export const fmtCurrency = (value, currency = "EUR") =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(value ?? 0);

export const fmtEUR = (value) => fmtCurrency(value, "EUR");

export const toNumberSafe = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const normalized = value.toString ? value.toString().replace(",", ".") : `${value}`;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const roundMoney = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export const getInvoicePaidAmount = (invoice) => {
    if (!invoice || invoice.paymentAmount === null || invoice.paymentAmount === undefined) return 0;
    const paid = toNumberSafe(invoice.paymentAmount);
    return paid > 0 ? roundMoney(paid) : 0;
};

export const getInvoiceOutstandingAmount = (invoice) => {
    if (!invoice || invoice.totalGross === null || invoice.totalGross === undefined) return null;
    const total = toNumberSafe(invoice.totalGross);
    const paid = getInvoicePaidAmount(invoice);
    const outstanding = total - paid;
    return outstanding > 0 ? roundMoney(outstanding) : 0;
};

export const resolveInvoicePaymentStatus = (invoice) => {
    if (invoice?.paid) return "paid";
    const paidAmount = getInvoicePaidAmount(invoice);
    if (paidAmount > 0) return "partial";
    return "open";
};

export const invoicePaymentStyles = {
    open: {
        border: "1px solid #f1b3b3",
        background: "linear-gradient(0deg, #fff0f0 0%, #fff 60%)",
    },
    partial: {
        border: "1px solid #ffd6a0",
        background: "linear-gradient(0deg, #fff3d6 0%, #fff 60%)",
    },
    paid: {
        border: "1px solid #b7e4c7",
        background: "linear-gradient(0deg, #eefaf1 0%, #fff 60%)",
    },
};

export const invoiceTypeLabels = {
    OFFER: "Angebot",
    STANDARD: "Standard",
    DOWN_PAYMENT: "Anzahlung",
    PARTIAL: "Teilrechnung",
    FINAL: "Schlussrechnung",
    CANCELLATION: "Storno",
};

export const invoiceTypeChipColor = {
    OFFER: "info",
    STANDARD: "default",
    DOWN_PAYMENT: "info",
    PARTIAL: "warning",
    FINAL: "success",
    CANCELLATION: "error",
};

export const paymentLinkStatusLabel = {
    NONE: "Kein Pay-Link",
    CREATED: "Pay-Link erstellt",
    SENT: "Pay-Link gesendet",
    PAID: "Pay-Link bezahlt",
    EXPIRED: "Pay-Link abgelaufen",
    FAILED: "Pay-Link fehlgeschlagen",
};

export const paymentLinkChipColor = {
    CREATED: "info",
    SENT: "info",
    PAID: "success",
    EXPIRED: "warning",
    FAILED: "error",
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const wizardSteps = ["Typ & Bezug", "Positionen", "Zusammenfassung"];

export const invoiceGroupStateKey = "invoiceManagementExpandedGroups";

export const getDefaultInvoiceGroupState = () => {
    const now = new Date();
    const yearKey = String(now.getFullYear());
    const monthKey = String(now.getMonth());
    return { years: { [yearKey]: true }, months: { [`${yearKey}-${monthKey}`]: true } };
};

export const readInvoiceGroupState = () => {
    if (typeof window === "undefined") return null;
    try {
        const raw = sessionStorage.getItem(invoiceGroupStateKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return {
            years: parsed?.years && typeof parsed.years === "object" ? parsed.years : {},
            months: parsed?.months && typeof parsed.months === "object" ? parsed.months : {},
        };
    } catch {
        return null;
    }
};

export const extractInvoices = (payload) => {
    const data = payload?.data ?? payload;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
};

export const buildMonthKey = (year, monthKey) => `${year}-${monthKey}`;

export const normalizeInvoiceMonths = (payload) => {
    if (!Array.isArray(payload)) return [];
    const normalized = [];
    payload.forEach((entry) => {
        const year = Number(entry?.year);
        const month = Number(entry?.month);
        if (!Number.isFinite(year) || !Number.isFinite(month)) return;
        const monthIndex = month - 1;
        if (monthIndex < 0 || monthIndex > 11) return;
        normalized.push({ year, month: monthIndex });
    });
    return normalized;
};

export const normalizeInvoiceArticles = (items) => {
    if (!Array.isArray(items)) return [];
    const regular = [];
    let shipping = null;
    items.forEach((item) => {
        if (item?.shippingCostId) {
            shipping = item;
        } else {
            regular.push(item);
        }
    });
    return shipping ? [...regular, shipping] : regular;
};

export const buildShippingCostArticle = (cost) => ({
    shippingCostId: cost.id,
    name: cost.name,
    price: cost.price,
    taxRate: cost.taxRate ?? 0,
    amount: 1,
    articleDiscount: 0,
    articleDiscountReason: "",
});
